/**
 * View Activity Template - Platform admin view for activity template details
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, AdminPageLayout, Card, Section, Grid, Col, Badge } from '../../components';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';

const ViewActivityTemplate = () => {
  const navigate = useNavigate();
  const { id: templateId } = useParams();
  const { showSuccess, showError } = useToast();

  const [template, setTemplate] = useState(null);
  const [linkedSkill, setLinkedSkill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine context from URL
  const location = useLocation();
  const currentPath = location.pathname;
  const { isPlatformAdmin, isClubAdmin, isBranchAdmin } = useMemo(() => ({
    isPlatformAdmin: currentPath.startsWith('/admin/system-templates'),
    isClubAdmin: currentPath.startsWith('/admin/templates'),
    isBranchAdmin: !!currentPath.match(/^\/branch\/[^/]+\/templates/)
  }), [currentPath]);
  const isCoach = currentPath.startsWith('/coach/templates');

  // Set back path
  let backPath;
  if (isPlatformAdmin) {
    backPath = '/admin/system-templates/activities';
  } else if (isClubAdmin) {
    backPath = '/admin/templates/activities';
  } else if (isBranchAdmin) {
    const branchId = currentPath.split('/')[2];
    backPath = `/branch/${branchId}/templates/activities`;
  } else if (isCoach) {
    backPath = '/coach/templates/activities';
  } else {
    backPath = '/admin/system-templates/activities';
  }

  const ACTIVITY_TYPE_LABELS = {
    'warmup': 'Warmup',
    'conditioning': 'Conditioning',
    'cooldown': 'Cooldown',
    'skill_building': 'Skill Building',
    'scrimmage': 'Scrimmage',
    'support': 'Support'
  };

  const loadTemplate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let templateData;
      if (isPlatformAdmin) {
        templateData = await apiService.get(`/api/admin/activity-templates/${templateId}`);
      } else if (isClubAdmin) {
        const userInfo = await apiService.getCurrentUser();
        const clubId = userInfo.club_id;
        templateData = await apiService.get(`/api/clubs/${clubId}/activity-templates/${templateId}`);
      } else if (isBranchAdmin) {
        const branchId = currentPath.split('/')[2];
        templateData = await apiService.get(`/api/branches/${branchId}/activity-templates/${templateId}`);
      } else if (isCoach) {
        const userInfo = await apiService.getCurrentUser();
        const branchId = userInfo.location_id;
        templateData = await apiService.get(`/api/branches/${branchId}/activity-templates/${templateId}`);
      }

      setTemplate(templateData);

      // Load linked skill if present
      if (templateData && templateData.skill_id) {
        try {
          let skillData;
          if (isPlatformAdmin) {
            skillData = await apiService.getSkillTemplate(templateData.skill_id);
          } else if (isClubAdmin || isBranchAdmin || isCoach) {
            // Club admins, branch admins, and coaches all load club-level skills
            skillData = await apiService.request(`/api/skills/club-skills/${templateData.skill_id}`);
          }
          setLinkedSkill(skillData);
        } catch (err) {
          // Skill not found or error - that's okay
          console.error('Failed to load linked skill:', err);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, isPlatformAdmin, isClubAdmin, isBranchAdmin, isCoach]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const handleActivate = async () => {
    try {
      if (isPlatformAdmin) {
        await apiService.put(`/api/admin/activity-templates/${templateId}`, { is_active: true });
      } else if (isClubAdmin) {
        const userInfo = await apiService.getCurrentUser();
        const clubId = userInfo.club_id;
        await apiService.put(`/api/clubs/${clubId}/activity-templates/${templateId}`, { is_active: true });
      } else if (isBranchAdmin) {
        const branchId = currentPath.split('/')[2];
        await apiService.put(`/api/branches/${branchId}/activity-templates/${templateId}`, { is_active: true });
      } else if (isCoach) {
        const userInfo = await apiService.getCurrentUser();
        const branchId = userInfo.location_id;
        await apiService.put(`/api/branches/${branchId}/activity-templates/${templateId}`, { is_active: true });
      }
      showSuccess('Activity template activated successfully');
      loadTemplate();
    } catch (err) {
      showError(`Failed to activate: ${err.message}`);
    }
  };

  const handleDeactivate = async () => {
    try {
      if (isPlatformAdmin) {
        await apiService.put(`/api/admin/activity-templates/${templateId}`, { is_active: false });
      } else if (isClubAdmin) {
        const userInfo = await apiService.getCurrentUser();
        const clubId = userInfo.club_id;
        await apiService.put(`/api/clubs/${clubId}/activity-templates/${templateId}`, { is_active: false });
      } else if (isBranchAdmin) {
        const branchId = currentPath.split('/')[2];
        await apiService.put(`/api/branches/${branchId}/activity-templates/${templateId}`, { is_active: false });
      } else if (isCoach) {
        const userInfo = await apiService.getCurrentUser();
        const branchId = userInfo.location_id;
        await apiService.put(`/api/branches/${branchId}/activity-templates/${templateId}`, { is_active: false });
      }
      showSuccess('Activity template deactivated successfully');
      loadTemplate();
    } catch (err) {
      showError(`Failed to deactivate: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout title="VIEW ACTIVITY" maxWidth="1400px">
        <div style={{ padding: '4rem', textAlign: 'center', opacity: 0.6 }}>Loading activity details...</div>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout title="VIEW ACTIVITY" maxWidth="1400px">
        <Section title="Error">
          <Card level={1}>
            <p style={{ color: 'var(--color-error)', marginBottom: '1.5rem' }}>Failed to load activity: {error}</p>
            <Button onClick={loadTemplate} variant="primary">Try Again</Button>
          </Card>
        </Section>
      </AdminPageLayout>
    );
  }

  if (!template) {
    return (
      <AdminPageLayout title="VIEW ACTIVITY" maxWidth="1400px">
        <Section title="Not Found">
          <Card level={1}>
            <p style={{ opacity: 0.6, marginBottom: '1.5rem' }}>This activity could not be found.</p>
            <Button onClick={() => navigate(backPath)} variant="outline">
              Back to Activities
            </Button>
          </Card>
        </Section>
      </AdminPageLayout>
    );
  }

  const editPath = isPlatformAdmin 
    ? `/admin/system-templates/activities/${templateId}/edit`
    : isClubAdmin
    ? `/admin/templates/activities/${templateId}/edit`
    : isBranchAdmin
    ? `/branch/${currentPath.split('/')[2]}/templates/activities/${templateId}/edit`
    : isCoach
    ? `/coach/templates/activities/${templateId}/edit`
    : `/admin/system-templates/activities/${templateId}/edit`;

  return (
    <AdminPageLayout
      title="VIEW ACTIVITY"
      subtitle={template.name}
      backPath={backPath}
      maxWidth="1400px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>

        <Section
          title="Status"
          actions={
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Button
                onClick={() => navigate(editPath)}
                variant="edit"
              >
                Edit Activity
              </Button>
              {template.is_active ? (
                <Button
                  onClick={handleDeactivate}
                  variant="warning"
                >
                  Deactivate
                </Button>
              ) : (
                <Button
                  onClick={handleActivate}
                  variant="success"
                >
                  Activate
                </Button>
              )}
            </div>
          }
        >
          <Card level={1}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'var(--surface-lvl2)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--color-border-light)'
              }}>
                <Badge variant={template.is_active ? 'success' : 'warning'} dot size="lg" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.25rem' }}>{template.name}</div>
                <div style={{ opacity: 0.6, fontSize: '0.95rem' }}>
                  {ACTIVITY_TYPE_LABELS[template.activity_type] || template.activity_type}
                </div>
              </div>
              <Badge variant={template.is_active ? 'success' : 'warning'} size="sm">
                {template.is_active ? 'ACTIVE' : 'INACTIVE'}
              </Badge>
            </div>
          </Card>
        </Section>

        <Grid columns={12} gap="2rem">
          <Col span={8}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <Section title="Details">
                <Card level={1}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Description</div>
                      <p style={{ lineHeight: 1.6, fontSize: '1rem' }}>{template.description || 'No description provided.'}</p>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', padding: '1.25rem', background: 'var(--surface-lvl2)', borderRadius: '12px' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Activity Type</div>
                        <div style={{ fontWeight: 600 }}>{ACTIVITY_TYPE_LABELS[template.activity_type] || template.activity_type}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Default Duration</div>
                        <div style={{ fontWeight: 600 }}>{template.default_duration_mins || 0} minutes</div>
                      </div>
                    </div>
                  </div>
                </Card>
              </Section>

              {linkedSkill && (
                <Section title="Linked Skill">
                  <Card level={1}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.5rem' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        background: 'var(--surface-lvl2)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid var(--color-border-light)'
                      }}>
                        ⚽
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{linkedSkill.name}</div>
                        <div style={{ fontSize: '0.85rem', opacity: 0.6 }}>
                          {linkedSkill.category?.replace('_', ' ') || 'Uncategorized'} • {linkedSkill.difficulty_level || 'No level'}
                        </div>
                      </div>
                      <Button
                        onClick={() => navigate(isPlatformAdmin ? `/admin/skill-templates/${linkedSkill.id}` : `/admin/skills/${linkedSkill.id}`)}
                        variant="outline"
                        size="sm"
                      >
                        View Skill
                      </Button>
                    </div>
                  </Card>
                </Section>
              )}
            </div>
          </Col>

          <Col span={4}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <Section title="Information">
                <Card level={1}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.75rem', borderBottom: '1px solid var(--color-border-light)' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Scope</span>
                      <span style={{ fontWeight: 600 }}>
                        <Badge variant="info" size="sm">{template.scope || 'platform'}</Badge>
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.75rem', borderBottom: '1px solid var(--color-border-light)' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Created</span>
                      <span style={{ fontWeight: 600 }}>{template.created_at ? new Date(template.created_at).toLocaleDateString() : 'Unknown'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Last Updated</span>
                      <span style={{ fontWeight: 600 }}>{template.updated_at ? new Date(template.updated_at).toLocaleDateString() : 'N/A'}</span>
                    </div>
                  </div>
                </Card>
              </Section>
            </div>
          </Col>
        </Grid>
      </div>
    </AdminPageLayout>
  );
};

export default ViewActivityTemplate;
